import React from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Button, Card } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import Subscription from "~/models/Subscription/Subscription";
import OtherProduct from "~/models/OtherProduct";
import SubscriptionTable from "~/modules/BillingCentral/components/SubscriptionTable";
import SubscriptionStatus from "~/constants/Subscription/SubscriptionStatus";
import Provider from "~/models/Provider";
import Customer from "~/models/Customer";
import SubscriptionCardHeader from "~/modules/BillingCentral/components/SubscriptionCardHeader";
import SubscriptionCancelAtPeriodEndCardDescription from "~/modules/BillingCentral/components/SubscriptionCancelAtPeriodEndCardDescription";
import SubscriptionStatusInactiveCardContext from "~/modules/BillingCentral/components/SubscriptionStatusInactiveCardContext";
import SubscriptionCanceledCardDescription from "~/modules/BillingCentral/components/SubscriptionCanceledCardDescription";

const SubscriptionCard = observer(
  ({
    t,
    subscription,
    otherProducts,
    applicableTaxes,
    subtotal,
    totalAmountWithTaxes,
    redirectToSubscriptionEditPage,
    redirectToInvoiceIndexPage,
    calculatePriceForDaysCallback,
    showCancelAtPeriodEndButton,
    showReactivateButton,
    showCancelAtPeriodEndConfirmationModalCallback,
    showCancelConfirmationModalCallback,
    actualUser,
  }) => (
    <Card fluid>
      <Card.Content>
        <SubscriptionCardHeader subscription={subscription} />
        {actualUser !== null && subscription.providerId !== null && (
          <Card.Description>
            Provider #{subscription.providerId} | {actualUser.name} ({actualUser.company})
          </Card.Description>
        )}
        {actualUser !== null && subscription.customerId !== null && (
          <Card.Description>
            Customer #{subscription.customerId} | {actualUser.firstName} {actualUser.lastName}
          </Card.Description>
        )}
        <SubscriptionCancelAtPeriodEndCardDescription subscription={subscription} />
        <SubscriptionCanceledCardDescription subscription={subscription} />
      </Card.Content>
      <SubscriptionStatusInactiveCardContext subscription={subscription} />
      <Card.Content extra>
        {typeof showCancelAtPeriodEndConfirmationModalCallback !== "undefined" &&
          ((!subscription.cancelAtPeriodEnd && showCancelAtPeriodEndButton) ||
            (subscription.cancelAtPeriodEnd && showReactivateButton)) &&
          (subscription.status === SubscriptionStatus.ACTIVE ||
            subscription.status === SubscriptionStatus.INACTIVE) && (
            <Button color="orange" onClick={showCancelAtPeriodEndConfirmationModalCallback}>
              {t(`billingCentral:${!subscription.cancelAtPeriodEnd ? "cancelAtPeriodEnd" : "reactivate"}`)}
            </Button>
          )}
        {typeof showCancelConfirmationModalCallback !== "undefined" &&
          (subscription.status === SubscriptionStatus.ACTIVE ||
            subscription.status === SubscriptionStatus.INACTIVE) && (
            <Button color="red" onClick={showCancelConfirmationModalCallback}>
              {t("cancel")}
            </Button>
          )}
        {typeof redirectToSubscriptionEditPage !== "undefined" &&
          !subscription.cancelAtPeriodEnd &&
          subscription.status === SubscriptionStatus.ACTIVE && (
            <Button primary onClick={redirectToSubscriptionEditPage}>
              {t("edit")}
            </Button>
          )}
        <Button secondary onClick={redirectToInvoiceIndexPage}>
          {t("billingCentral:invoicesAndPayments")}
        </Button>
      </Card.Content>
      <Card.Content extra>
        <SubscriptionTable
          subscriptionItems={subscription.nextBillingCycleSubscriptionItems}
          otherProducts={otherProducts}
          applicableTaxes={applicableTaxes}
          subtotal={subtotal}
          totalAmountWithTaxes={totalAmountWithTaxes}
          calculatePriceForDaysCallback={calculatePriceForDaysCallback}
        />
      </Card.Content>
    </Card>
  ),
);

SubscriptionCard.propTypes = {
  t: PropTypes.func.isRequired,
  subscription: PropTypes.instanceOf(Subscription),
  otherProducts: PropTypes.arrayOf(PropTypes.instanceOf(OtherProduct)),
  applicableTaxes: PropTypes.array.isRequired,
  subtotal: PropTypes.number.isRequired,
  totalAmountWithTaxes: PropTypes.number.isRequired,
  redirectToSubscriptionEditPage: PropTypes.func,
  redirectToInvoiceIndexPage: PropTypes.func.isRequired,
  calculatePriceForDaysCallback: PropTypes.func.isRequired,
  showCancelAtPeriodEndButton: PropTypes.bool,
  showReactivateButton: PropTypes.bool,
  showCancelAtPeriodEndConfirmationModalCallback: PropTypes.func,
  showCancelConfirmationModalCallback: PropTypes.func,
  actualUser: PropTypes.oneOfType([PropTypes.instanceOf(Provider), PropTypes.instanceOf(Customer)]),
};

SubscriptionCard.defaultProps = {
  actualUser: null,
  showCancelAtPeriodEndButton: false,
  showReactivateButton: true,
};

export default withTranslation()(SubscriptionCard);
